import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type { Coordinates, MapBounds, MapViewState } from '../types';

// Refined map state interface
interface MapState {
  // Core map properties
  viewState: MapViewState;
  bounds: MapBounds | null;
  
  // Map interaction state
  isInteracting: boolean;
  lastInteraction: number | null;
  
  // Map configuration
  config: {
    minZoom: number;
    maxZoom: number;
    maxBounds: [[number, number], [number, number]] | null;
    style: string;
  };
  
  // Error handling
  error: string | null;
  isLoading: boolean;
}

interface MapActions {
  // View state management
  setCenter: (center: Coordinates) => void;
  setZoom: (zoom: number) => void;
  setBearing: (bearing: number) => void;
  setPitch: (pitch: number) => void;
  setViewState: (viewState: Partial<MapViewState>) => void;
  setBounds: (bounds: MapBounds) => void;
  
  // Map configuration
  updateConfig: (config: Partial<MapState['config']>) => void;
  setMapStyle: (style: string) => void;
  
  // Interaction tracking
  setInteracting: (interacting: boolean) => void;
  recordInteraction: () => void;
  
  // Error handling
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  clearError: () => void;
  
  // Utility actions
  fitToBounds: (bounds: MapBounds, padding?: number) => void;
  flyTo: (center: Coordinates, zoom?: number) => void;
  resetView: () => void;
  resetMap: () => void;
  
  // Computed getters
  getCurrentView: () => MapViewState;
  getMapConfig: () => MapState['config'];
}

type MapStore = MapState & MapActions;

// Default map configuration from environment variables
const defaultCenter: Coordinates = [
  parseFloat(import.meta.env.VITE_PROJECT_CENTER_LNG || '-74.0431'),
  parseFloat(import.meta.env.VITE_PROJECT_CENTER_LAT || '40.8776'),
];

const initialState: MapState = {
  viewState: {
    center: defaultCenter,
    zoom: 12,
    bearing: 0,
    pitch: 0
  },
  bounds: null,
  isInteracting: false,
  lastInteraction: null,
  config: {
    minZoom: 10,
    maxZoom: 20,
    maxBounds: [[-74.1, 40.8], [-74.0, 40.95]],
    style: 'https://demotiles.maplibre.org/style.json'
  },
  error: null,
  isLoading: false
};

export const useMapStore = create<MapStore>()(
  devtools(
    subscribeWithSelector(
      (set, get) => ({
        ...initialState,

        // View state management
        setCenter: (center) =>
          set(
            (state) => ({
              viewState: { ...state.viewState, center },
              error: null
            }),
            false,
            'mapStore/setCenter'
          ),

        setZoom: (zoom) =>
          set(
            (state) => ({
              viewState: { ...state.viewState, zoom: Math.max(state.config.minZoom, Math.min(state.config.maxZoom, zoom)) },
              error: null
            }),
            false,
            'mapStore/setZoom'
          ),

        setBearing: (bearing) =>
          set(
            (state) => ({
              viewState: { ...state.viewState, bearing },
              error: null
            }),
            false,
            'mapStore/setBearing'
          ),

        setPitch: (pitch) =>
          set(
            (state) => ({
              viewState: { ...state.viewState, pitch: Math.max(0, Math.min(60, pitch)) },
              error: null
            }),
            false,
            'mapStore/setPitch'
          ),

        setViewState: (viewState) =>
          set(
            (state) => ({
              viewState: { ...state.viewState, ...viewState },
              error: null
            }),
            false,
            'mapStore/setViewState'
          ),

        setBounds: (bounds) =>
          set(
            { bounds, error: null },
            false,
            'mapStore/setBounds'
          ),

        // Map configuration
        updateConfig: (config) =>
          set(
            (state) => ({
              config: { ...state.config, ...config },
              error: null
            }),
            false,
            'mapStore/updateConfig'
          ),

        setMapStyle: (style) =>
          set(
            (state) => ({
              config: { ...state.config, style },
              error: null
            }),
            false,
            'mapStore/setMapStyle'
          ),

        // Interaction tracking
        setInteracting: (interacting) =>
          set(
            { isInteracting: interacting },
            false,
            'mapStore/setInteracting'
          ),

        recordInteraction: () =>
          set(
            { lastInteraction: Date.now() },
            false,
            'mapStore/recordInteraction'
          ),

        // Error handling
        setError: (error) =>
          set(
            { error, isLoading: false },
            false,
            'mapStore/setError'
          ),

        setLoading: (loading) =>
          set(
            { isLoading: loading },
            false,
            'mapStore/setLoading'
          ),

        clearError: () =>
          set(
            { error: null },
            false,
            'mapStore/clearError'
          ),

        // Utility actions
        fitToBounds: (bounds, padding = 50) => {
          // Calculate center and zoom from bounds
          const centerLng = (bounds.southwest[0] + bounds.northeast[0]) / 2;
          const centerLat = (bounds.southwest[1] + bounds.northeast[1]) / 2;
          
          // Simple zoom calculation based on bounds size
          const lngDiff = Math.abs(bounds.northeast[0] - bounds.southwest[0]);
          const latDiff = Math.abs(bounds.northeast[1] - bounds.southwest[1]);
          const maxDiff = Math.max(lngDiff, latDiff);
          
          let zoom = 12;
          if (maxDiff > 0.1) zoom = 8;
          else if (maxDiff > 0.05) zoom = 10;
          else if (maxDiff > 0.01) zoom = 12;
          else zoom = 14;

          const { config } = get();
          zoom = Math.max(config.minZoom, Math.min(config.maxZoom, zoom));

          set(
            (state) => ({
              viewState: {
                ...state.viewState,
                center: [centerLng, centerLat],
                zoom
              },
              bounds,
              error: null
            }),
            false,
            'mapStore/fitToBounds'
          );
        },

        flyTo: (center, zoom) => {
          const { viewState, config } = get();
          const finalZoom = zoom !== undefined 
            ? Math.max(config.minZoom, Math.min(config.maxZoom, zoom))
            : viewState.zoom;

          set(
            (state) => ({
              viewState: {
                ...state.viewState,
                center,
                zoom: finalZoom
              },
              error: null
            }),
            false,
            'mapStore/flyTo'
          );
        },

        resetView: () =>
          set(
            (state) => ({
              viewState: {
                center: defaultCenter,
                zoom: 12,
                bearing: 0,
                pitch: 0
              },
              bounds: null,
              error: null
            }),
            false,
            'mapStore/resetView'
          ),

        resetMap: () =>
          set(
            initialState,
            false,
            'mapStore/resetMap'
          ),

        // Computed getters
        getCurrentView: () => get().viewState,
        getMapConfig: () => get().config,
      })
    ),
    {
      name: 'map-store',
    }
  )
);

// Specialized selectors
export const useMapViewState = () => 
  useMapStore(state => state.viewState);

export const useMapCenter = () => 
  useMapStore(state => state.viewState.center);

export const useMapZoom = () => 
  useMapStore(state => state.viewState.zoom);

export const useMapBounds = () => 
  useMapStore(state => state.bounds);

export const useMapConfig = () => 
  useMapStore(state => state.config);

export const useMapError = () => 
  useMapStore(state => state.error);

export const useMapLoading = () => 
  useMapStore(state => state.isLoading);

export const useMapInteraction = () => 
  useMapStore(state => ({
    isInteracting: state.isInteracting,
    lastInteraction: state.lastInteraction
  }));

// Action selectors
export const useMapActions = () => 
  useMapStore(state => ({
    setCenter: state.setCenter,
    setZoom: state.setZoom,
    setBearing: state.setBearing,
    setPitch: state.setPitch,
    setViewState: state.setViewState,
    setBounds: state.setBounds,
    updateConfig: state.updateConfig,
    setMapStyle: state.setMapStyle,
    fitToBounds: state.fitToBounds,
    flyTo: state.flyTo,
    resetView: state.resetView,
    resetMap: state.resetMap,
    setError: state.setError,
    clearError: state.clearError
  }));

// Backward compatibility selectors (deprecated)
export const useSelectedLayers = () => {
  console.warn('useSelectedLayers is deprecated. Use useLayerStore from layerStore instead.');
  return {};
};

export const useSelectedTree = () => {
  console.warn('useSelectedTree is deprecated. Use useTreeStore from treeStore instead.');
  return null;
};

export const useAssessmentMode = () => {
  console.warn('useAssessmentMode is deprecated. Use useGeocodingStore from geocodingStore instead.');
  return false;
};

export const useAssessmentResult = () => {
  console.warn('useAssessmentResult is deprecated. Use useGeocodingStore from geocodingStore instead.');
  return null;
};
